import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTokenErrorHandler } from "~/shared/hooks/useTokenErrorHandler";
import { useServerSearch } from "~/shared/hooks/useServerSearch";
import { usePersistedFilter } from "~/shared/hooks/usePersistedFilter";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { usePostedOrderWebSocket, type PostedOrderWsEvent } from "~/features/posted-orders/hooks";
import type { PostedOrder, PostedOrdersPagination } from "~/features/posted-orders/types";
import { PostedOrdersTable } from "~/features/posted-orders/components/PostedOrdersTable";
import { PostedOrderFailedPanel } from "~/features/posted-orders/components/PostedOrderFailedPanel";
import { CenteredLoading } from "~/shared/components/CenteredLoading";
import { ErrorState } from "~/shared/components/ErrorState";
import { fetchPostedOrders, postOrdersToNav } from "~/features/posted-orders/api";
import { configureMsalInstance } from "~/services/api-client";
import { useLocationCode } from "~/shared/contexts/LocationCodeContext";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const;

/** Map WebSocket payload to PostedOrder */
function mapWsPayloadToPostedOrder(p: Record<string, unknown>): PostedOrder {
  return {
    smNumber: (p.smNumber as string) ?? "",
    patientId: (p.patientId as string) ?? "",
    created: (p.created as string) ?? "",
    size: p.size as number | undefined,
    whiteGlove: (p.whiteGlove as boolean) ?? false,
    outcome: (p.outcome as string) ?? "",
    navStatus: (p.navStatus as string) ?? "pending",
    jobLocationId: (p.jobLocationId as string) ?? "",
    navError: p.navError as string | undefined,
  };
}

export default function PostedOrdersPage() {
  const { instance: msalInstance, accounts } = useMsal();
  const { jobLocations } = useDeliveryWindows();
  const { selectedLocationCode } = useLocationCode();
  const selectedLocationCodeRef = useRef(selectedLocationCode);
  const [items, setItems] = useState<PostedOrder[]>([]);
  const allCitiesSeenRef = useRef<Set<string>>(new Set());
  const [allCities, setAllCities] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<typeof PAGE_SIZE_OPTIONS[number]>(PAGE_SIZE_OPTIONS[4]);
  const [pagination, setPagination] = useState<PostedOrdersPagination>({
    total: 0,
    page: 1,
    limit: PAGE_SIZE_OPTIONS[4],
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFailedItem, setSelectedFailedItem] = useState<PostedOrder | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Server-side column filters
  const [cityFilter, setCityFilter, cityFilterRef] = usePersistedFilter('po:city');
  const [outcomeFilter, setOutcomeFilter] = usePersistedFilter('po:outcome');
  const [navStatusFilter, setNavStatusFilter] = usePersistedFilter('po:navStatus');
  const [whiteGloveFilter, setWhiteGloveFilter] = usePersistedFilter('po:whiteGlove');

  const [selectedRowOffset, setSelectedRowOffset] = useState(0);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const isInitialMount = useRef(true);
  const wsRefreshTimerRef = useRef<number | null>(null);

  const { handleTokenError, resetTokenErrorRetry } = useTokenErrorHandler(msalInstance);

  const currentUserId = useMemo(() => {
    const email = accounts[0]?.username ?? '';
    const prefix = email.split('@')[0]?.toUpperCase() ?? '';
    return prefix ? `BAYSHORE\\${prefix}` : '';
  }, [accounts]);


  function buildFilters() {
    return {
      cities: cityFilterRef.current.length > 0 ? cityFilterRef.current : undefined,
      locationCodes: selectedLocationCodeRef.current ? [selectedLocationCodeRef.current] : undefined,
    };
  }

  const loadPostedOrders = useCallback(async (reqPage?: number, reqLimit?: number) => {
    if (isLoadingRef.current) return;
    const p = reqPage ?? page;
    const lim = reqLimit ?? pageSize;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    isLoadingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      const { items: data, pagination: pag, contextualCities } = await fetchPostedOrders(
        p,
        lim,
        searchQueryRef.current ?? undefined,
        buildFilters()
      );
      setItems(data);
      setPagination(pag);
      contextualCities.forEach(c => allCitiesSeenRef.current.add(c));
      setAllCities(Array.from(allCitiesSeenRef.current).sort((a, b) => a.localeCompare(b)));
      setPage(pag.page);
      setError(null);
      resetTokenErrorRetry();
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === "AbortError" || err.message.includes("cancel"))) return;
      if (handleTokenError(err, () => loadPostedOrders(p, lim), () => { isLoadingRef.current = false; })) return;
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [handleTokenError, page, pageSize, resetTokenErrorRetry]);

  const handlePostedOrderWsEvent = useCallback((event: PostedOrderWsEvent) => {
    const incoming = mapWsPayloadToPostedOrder(event.postedOrder);
    if (!incoming.smNumber) return;
    // With server pagination, this page only holds a slice of the full dataset.
    // Update in-place if the row is on this page; otherwise schedule a refresh.
    setItems((prev) => {
      const exists = prev.some((r) => r.smNumber === incoming.smNumber);
      if (event.action === 'deleted') {
        return exists ? prev.filter((r) => r.smNumber !== incoming.smNumber) : prev;
      }
      if (exists) {
        return prev.map((r) => (r.smNumber === incoming.smNumber ? { ...r, ...incoming } : r));
      }
      return prev;
    });

    if (wsRefreshTimerRef.current != null) window.clearTimeout(wsRefreshTimerRef.current);
    wsRefreshTimerRef.current = window.setTimeout(() => {
      loadPostedOrders(page, pageSize);
      wsRefreshTimerRef.current = null;
    }, 300);
  }, [loadPostedOrders, page, pageSize]);

  usePostedOrderWebSocket({
    userId: currentUserId,
    jobLocations: jobLocations.length ? jobLocations : undefined,
    onPostedOrderEvent: handlePostedOrderWsEvent,
    enabled: true,
  });

  useEffect(() => {
    configureMsalInstance(msalInstance);
  }, [msalInstance]);

  useEffect(() => {
    loadPostedOrders(1, PAGE_SIZE_OPTIONS[4]);
    return () => {
      abortControllerRef.current?.abort();
      if (wsRefreshTimerRef.current != null) window.clearTimeout(wsRefreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    loadPostedOrders(page, pageSize);
  }, [page, pageSize, loadPostedOrders]);

  useEffect(() => { selectedLocationCodeRef.current = selectedLocationCode; }, [selectedLocationCode]);

  // Ref to always hold the latest load for filter/search triggers
  const loadRef = useRef(loadPostedOrders);
  useEffect(() => { loadRef.current = loadPostedOrders; }, [loadPostedOrders]);

  // Re-fetch when location changes
  const prevLocationRef = useRef(selectedLocationCode);
  useEffect(() => {
    if (prevLocationRef.current === selectedLocationCode) return;
    prevLocationRef.current = selectedLocationCode;
    loadRef.current(1, pageSize);
  }, [selectedLocationCode, pageSize]);

  // Server-side search (debounced input + immediate Enter)
  const { searchQuery, searchQueryRef, handleSearchChange, handleSearchSubmit } = useServerSearch({
    onSearch: () => loadRef.current(1, pageSize),
  });

  // Handle city filter change
  const handleCityFilterChange = useCallback((selected: string[]) => {
    setCityFilter(selected);
    loadRef.current(1, pageSize);
  }, [pageSize, setCityFilter]);

  // Handle outcome filter change — client-side only, no API reload
  const handleOutcomeFilterChange = useCallback((selected: string[]) => {
    setOutcomeFilter(selected);
  }, [setOutcomeFilter]);

  // Handle NAV status filter change — client-side only, no API reload
  const handleNavStatusFilterChange = useCallback((selected: string[]) => {
    setNavStatusFilter(selected);
  }, [setNavStatusFilter]);

  // Handle whiteGlove filter change — client-side only, no API reload
  const handleWhiteGloveFilterChange = useCallback((selected: string[]) => {
    setWhiteGloveFilter(selected);
  }, [setWhiteGloveFilter]);

  const selectedSmNumber = selectedFailedItem?.smNumber ?? null;

  useEffect(() => {
    if (!selectedSmNumber || !tableScrollRef.current || !contentAreaRef.current) {
      setSelectedRowOffset(0);
      return;
    }
    const measure = () => {
      const row = tableScrollRef.current?.querySelector<HTMLTableRowElement>(
        `tr[data-sm-number="${selectedSmNumber}"]`
      );
      const contentEl = contentAreaRef.current;
      const panelEl = panelRef.current;
      if (row && contentEl) {
        const rowTop = row.getBoundingClientRect().top;
        const contentTop = contentEl.getBoundingClientRect().top;
        const desiredOffset = Math.max(0, rowTop - contentTop);
        const panelHeight = panelEl?.getBoundingClientRect().height ?? 0;
        const maxOffset = Math.max(0, contentEl.clientHeight - panelHeight);
        setSelectedRowOffset(Math.min(desiredOffset, maxOffset));
      } else {
        setSelectedRowOffset(0);
      }
    };
    measure();
    requestAnimationFrame(measure);
    const scrollEl = tableScrollRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;

    scrollEl?.addEventListener("scroll", measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    if (contentAreaRef.current) resizeObserver?.observe(contentAreaRef.current);
    if (panelRef.current) resizeObserver?.observe(panelRef.current);
    return () => {
      scrollEl?.removeEventListener("scroll", measure);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, [selectedSmNumber, retrying]);

  const handleFailedItemClick = (item: PostedOrder) => {
    setSelectedFailedItem(item);
  };

  const handleClosePanel = () => {
    setSelectedFailedItem(null);
  };

  const handleRetry = async () => {
    if (!selectedFailedItem) return;
    try {
      setRetrying(true);
      setError(null);
      await postOrdersToNav([selectedFailedItem.smNumber]);
      await loadPostedOrders(page, pageSize);
      setError(null);
      setSelectedFailedItem(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to retry post to NAV");
    } finally {
      setRetrying(false);
    }
  };

  const totalPages = pagination.totalPages;
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const displayCount = `${pagination.total} total orders`;

  function getPageNumbers(): Array<number | '…'> {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }

  function navigatePage(newPage: number) {
    if (newPage === page) return;
    setPage(newPage);
  }

  return (
    <div
      className="posted-orders"
      style={{
        position: 'fixed',
        top: 'calc(var(--app-nav-header-height) + var(--app-nav-height))',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--neutral-gray)',
        padding: '8px 32px 8px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      <div className="page-header" style={{ flexShrink: 0, marginBottom: 8 }}>
        <h1 className="page-title">📤 Posted Orders</h1>
        <p className="page-subtitle">Orders posted to NAV</p>
      </div>

      {loading && items.length === 0 && !error && (
        <CenteredLoading />
      )}

      {error && !loading && (
        <ErrorState message={error} onRetry={() => loadPostedOrders(page, pageSize)} />
      )}

      {!(loading && items.length === 0) && !error && (
        <div ref={contentAreaRef} style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <PostedOrdersTable
              ref={tableScrollRef}
              items={items}
              loading={loading}
              onFailedItemClick={handleFailedItemClick}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onSearchSubmit={handleSearchSubmit}
              cityFilter={cityFilter}
              onCityFilterChange={handleCityFilterChange}
              cityOptions={allCities}
              outcomeFilter={outcomeFilter}
              onOutcomeFilterChange={handleOutcomeFilterChange}
              navStatusFilter={navStatusFilter}
              onNavStatusFilterChange={handleNavStatusFilterChange}
              whiteGloveFilter={whiteGloveFilter}
              onWhiteGloveFilterChange={handleWhiteGloveFilterChange}
            />
          </div>
          {selectedFailedItem && (
            <div style={{ width: 400, flexShrink: 0 }}>
              <div style={{ paddingTop: selectedRowOffset }}>
                <div ref={panelRef}>
                  <PostedOrderFailedPanel
                    item={selectedFailedItem}
                    onClose={handleClosePanel}
                    onRetry={handleRetry}
                    retrying={retrying}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="autocall-pagination"
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            padding: '10px 0',
            borderTop: '1px solid var(--border)',
            background: 'var(--neutral-gray)',
          }}
        >
          <span style={{ fontWeight: 500 }}>Orders per page:</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <button
                key={size}
                type="button"
                className={pageSize === size ? 'primary' : 'secondary'}
                onClick={() => { setPageSize(size); navigatePage(1); }}
                style={{ minWidth: 40 }}
              >
                {size}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <button
              type="button"
              className="secondary"
              onClick={() => navigatePage(Math.max(1, page - 1))}
              disabled={!canPrev}
              style={{ minWidth: 36 }}
            >
              ‹
            </button>
            {getPageNumbers().map((pg, i, arr) =>
              pg === '…' ? (
                <span key={`ellipsis-${arr[i - 1]}-${arr[i + 1]}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
              ) : (
                <button
                  key={pg}
                  type="button"
                  className={page === pg ? 'primary' : 'secondary'}
                  onClick={() => navigatePage(pg)}
                  style={{ minWidth: 36 }}
                >
                  {pg}
                </button>
              )
            )}
            <button
              type="button"
              className="secondary"
              onClick={() => navigatePage(Math.min(totalPages, page + 1))}
              disabled={!canNext}
              style={{ minWidth: 36 }}
            >
              ›
            </button>
          </div>

          <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {displayCount}
          </span>

          <button
            type="button"
            className="secondary"
            onClick={() => loadPostedOrders(page, pageSize)}
            aria-label="Refresh"
          >
            Refresh
          </button>
      </div>

    </div>
  );
}
